import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../firebase/config';

const PasswordReset = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extraire le code de l'URL (compatible avec HashRouter)
  const getUrlParams = () => {
    // Pour HashRouter, les paramètres peuvent être dans location.search ou après le hash
    let searchParams = location.search;
    
    // Si pas de paramètres dans location.search, vérifier dans l'URL complète
    if (!searchParams && window.location.href.includes('?')) {
      const fullUrl = window.location.href;
      const queryStart = fullUrl.indexOf('?');
      searchParams = fullUrl.substring(queryStart);
    }
    
    
    return new URLSearchParams(searchParams);
  };
  
  const urlParams = getUrlParams();
  const oobCode = urlParams.get('oobCode');
  const mode = urlParams.get('mode');

  useEffect(() => {
    const verifyCode = async () => {
      
      if (!oobCode || mode !== 'resetPassword') {
        setError('Lien de réinitialisation invalide');
        setVerifying(false);
        return;
      }

      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmail(email);
        setVerifying(false);
      } catch (error) {
        console.error('❌ [PasswordReset] Erreur de vérification:', error);
        
        let errorMessage = 'Lien de réinitialisation invalide ou expiré';
        if (error.code === 'auth/expired-action-code') {
          errorMessage = 'Ce lien de réinitialisation a expiré. Demandez un nouveau lien.';
        } else if (error.code === 'auth/invalid-action-code') {
          errorMessage = 'Ce lien de réinitialisation est invalide ou a déjà été utilisé.';
        }
        
        setError(errorMessage);
        setVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      
      setSuccess(true);
      
      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('❌ [PasswordReset] Erreur lors de la réinitialisation:', error);
      
      let errorMessage = 'Erreur lors de la réinitialisation du mot de passe';
      if (error.code === 'auth/expired-action-code') {
        errorMessage = 'Ce lien de réinitialisation a expiré. Demandez un nouveau lien.';
      } else if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'Ce lien de réinitialisation est invalide ou a déjà été utilisé.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le mot de passe est trop faible. Utilisez au moins 6 caractères.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Vérification du lien...
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (error && !email) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              Lien invalide
            </Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
            >
              Retour à la connexion
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              Succès !
            </Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la page de connexion.
            </Alert>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Nouveau mot de passe
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Définissez un nouveau mot de passe pour {email}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="Nouveau mot de passe"
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirmer le mot de passe"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Réinitialiser le mot de passe'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default PasswordReset;
